import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Autocomplete, Badge, Box, Button, CardActionArea, Chip, CircularProgress, Collapse,
  FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import {
  Add, CategoryOutlined, Clear, ExpandLess,
  ExpandMore, FilterList, Inventory2Outlined, QrCodeScanner, Search,
  UploadFileOutlined, OpenInNew,
} from '@mui/icons-material';
import { flexRender, getCoreRowModel, useReactTable, SortingState } from '@tanstack/react-table';
import { Item, Category, Company, InventoryMode, Role, PaginatedResponse, SearchParams } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import HelpTooltip from '../common/HelpTooltip';
import DataTableHeaderCell from '../common/DataTableHeaderCell';
import { useIsMobileShell } from '../common/useIsMobileShell';
import PageHeader from '../common/PageHeader';
import PageHeaderActionButton from '../common/PageHeaderActionButton';
import PaginationFooter from '../common/PaginationFooter';
import ListSurface from '../common/ListSurface';
import TableFilterPanel from '../common/TableFilterPanel';
import ColumnVisibilityMenu from '../common/ColumnVisibilityMenu';
import NotebookColumnTab from '../common/NotebookColumnTab';
import MobileListCard from '../common/MobileListCard';
import { apiClient } from '../../services/api';
import { InventoryManagementDialog } from '../inventory';
import { getColumnKey } from '../../utils/table';
import { sortingFromTableSort, tableSortFromSorting } from '../../utils/tableSort';
import { getItemPriceLabel, useItemColumns } from './ItemColumns';
import ItemActionsMenu from './ItemActionsMenu';
import { ItemHelpContent } from './ItemHelpContent';
import { BarcodeScanButton, BarcodeScannerDialog } from '../barcode';
import { useMobileTopBarAction, useMobileTopBarActions } from '../common/MobileTopBarActionsContext';
import { getInventoryMode } from '../../utils/inventoryMode';

interface ItemListProps {
  paginatedData: PaginatedResponse<Item> | null;
  loading: boolean;
  searchParams: SearchParams;
  onSearch: (searchTerm: string) => void;
  onSearchParamsChange: (params: Partial<SearchParams>) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSortChange: (sort?: string, direction?: 'asc' | 'desc') => void;
  onEdit: (item: Item) => void;
  onDelete: (item: Item) => void;
  onAdd: () => void;
  onBulkUpload: () => void;
  onCompanyFilter?: (companyId: string) => void;
  onAddCategory?: () => void;
  onEditCategory?: (category: Category) => void;
  onDeleteCategory?: (category: Category) => void;
  onRefresh?: () => void;
  autoFocusSearch?: boolean;
  batchTrackingEnabled?: boolean;
}

export interface ItemListRef {
  focusSearch: () => void;
  refresh: () => void;
}

const ItemList = forwardRef<ItemListRef, ItemListProps>(({
  paginatedData, loading, searchParams, onSearch, onSearchParamsChange,
  onPageChange, onPageSizeChange, onSortChange, onEdit, onDelete, onAdd,
  onBulkUpload, onCompanyFilter, onAddCategory, onRefresh, autoFocusSearch = false,
  batchTrackingEnabled,
}, ref) => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const isMobile = useIsMobileShell();
  const mobileTopBarActions = useMobileTopBarActions();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState(searchParams.categoryId || '');
  const [minPriceDraft, setMinPriceDraft] = useState(searchParams.minPrice?.toString() || '');
  const [maxPriceDraft, setMaxPriceDraft] = useState(searchParams.maxPrice?.toString() || '');
  const [companyDraft, setCompanyDraft] = useState(searchParams.companyId || '');
  const [filterError, setFilterError] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
  const [inventoryItem, setInventoryItem] = useState<Item | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    name: true, price: true, stock: true, categories: true, companyName: true, actions: true,
  });
  const canWrite = user?.role === Role.COMPANY_ADMIN;
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;
  const items = paginatedData?.content ?? [];
  const selectedCategory = categories.find(category => category.id === searchParams.categoryId) ?? null;
  const hasFilters = Boolean(searchParams.name || searchParams.categoryId || searchParams.minPrice !== undefined || searchParams.maxPrice !== undefined || searchParams.companyId);
  const appliedFilterCount = [searchParams.categoryId, searchParams.minPrice, searchParams.maxPrice, searchParams.companyId]
    .filter(value => value !== undefined && value !== '').length;

  useEffect(() => { setCategoryDraft(searchParams.categoryId || ''); }, [searchParams.categoryId]);
  useEffect(() => { setMinPriceDraft(searchParams.minPrice?.toString() || ''); }, [searchParams.minPrice]);
  useEffect(() => { setMaxPriceDraft(searchParams.maxPrice?.toString() || ''); }, [searchParams.maxPrice]);
  useEffect(() => { setCompanyDraft(searchParams.companyId || ''); }, [searchParams.companyId]);

  useMobileTopBarAction(mobileTopBarActions && canWrite ? {
    id: 'items-manage-categories', label: t('category.manage'), icon: <CategoryOutlined fontSize="small" />,
    order: 20, onSelect: () => onAddCategory?.(),
  } : null);
  useMobileTopBarAction(mobileTopBarActions && canWrite ? {
    id: 'items-bulk-upload', label: t('items.bulkUpload'), icon: <UploadFileOutlined fontSize="small" />,
    order: 30, onSelect: () => onBulkUpload(),
  } : null);

  const loadCategories = useCallback(async () => {
    try { setCategories(await apiClient.getCategories()); } catch { /* List remains usable without filters. */ }
  }, []);
  useImperativeHandle(ref, () => ({
    focusSearch: () => searchInputRef.current?.focus(),
    refresh: loadCategories,
  }), [loadCategories]);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    if (!isSuperAdmin) return;
    apiClient.getCompanies().then(setCompanies).catch(() => { /* Optional filter. */ });
  }, [isSuperAdmin]);

  const handleInventoryItemUpdated = (updatedItem: Item) => {
    setInventoryItem(updatedItem);
    onRefresh?.();
  };
  const openInventory = useCallback((item: Item) => setInventoryItem(item), []);
  const allColumns = useItemColumns({
    translate: t, canWrite, isSuperAdmin, onEdit, onDelete, onManageInventory: openInventory,
    onCategoryFilter: categoryId => onSearchParamsChange({ categoryId: searchParams.categoryId === categoryId ? undefined : categoryId, page: 0 }),
    activeCategoryId: searchParams.categoryId,
    batchTrackingEnabled,
  });
  const columns = useMemo(() => allColumns.filter(column => {
    const key = getColumnKey(column);
    return key === undefined || columnVisibility[key] !== false;
  }), [allColumns, columnVisibility]);
  const sorting: SortingState = sortingFromTableSort({
    sort: searchParams.sort === 'totalStockQuantity' ? 'stock' : searchParams.sort,
    direction: searchParams.direction,
  });
  const table = useReactTable({
    data: items, columns, state: { sorting }, manualSorting: true,
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const nextSort = tableSortFromSorting(next);
      onSortChange(nextSort.sort === 'stock' ? 'totalStockQuantity' : nextSort.sort, nextSort.direction);
    },
    getCoreRowModel: getCoreRowModel(), sortDescFirst: false,
  });
  const clearFilters = () => {
    setCategoryDraft(''); setMinPriceDraft(''); setMaxPriceDraft(''); setCompanyDraft(''); setFilterError('');
    onSearch('');
    onSearchParamsChange({ categoryId: undefined, minPrice: undefined, maxPrice: undefined, companyId: undefined, page: 0 });
  };

  const applyFilters = () => {
    const minPrice = minPriceDraft.trim() ? Number(minPriceDraft) : undefined;
    const maxPrice = maxPriceDraft.trim() ? Number(maxPriceDraft) : undefined;
    if ((minPrice !== undefined && (!Number.isFinite(minPrice) || minPrice < 0))
      || (maxPrice !== undefined && (!Number.isFinite(maxPrice) || maxPrice < 0))
      || (minPrice !== undefined && maxPrice !== undefined && minPrice > maxPrice)) {
      setFilterError(t('items.invalidPriceRange'));
      return;
    }
    setFilterError('');
    onSearchParamsChange({ categoryId: categoryDraft || undefined, minPrice, maxPrice, companyId: companyDraft || undefined, page: 0 });
    setShowFilters(false);
  };

  const activeFilterChips = [
    selectedCategory ? {
      key: 'category', label: selectedCategory.name,
      onDelete: () => onSearchParamsChange({ categoryId: undefined, page: 0 }),
    } : null,
    searchParams.minPrice !== undefined ? {
      key: 'minPrice', label: `${t('items.minPrice')}: ${searchParams.minPrice}`,
      onDelete: () => onSearchParamsChange({ minPrice: undefined, page: 0 }),
    } : null,
    searchParams.maxPrice !== undefined ? {
      key: 'maxPrice', label: `${t('items.maxPrice')}: ${searchParams.maxPrice}`,
      onDelete: () => onSearchParamsChange({ maxPrice: undefined, page: 0 }),
    } : null,
    searchParams.companyId ? {
      key: 'company', label: companies.find(company => company.id === searchParams.companyId)?.name || t('items.company'),
      onDelete: () => onCompanyFilter?.(''),
    } : null,
  ].filter((chip): chip is { key: string; label: string; onDelete: () => void } => Boolean(chip));

  return <Box sx={{ pb: isMobile && paginatedData ? 'calc(61px + env(safe-area-inset-bottom))' : 0 }}>
    <PageHeader
      title={t('items.title')}
      icon={<Inventory2Outlined />}
      accent="items"
      mobileMode="hidden"
      titleAccessory={<HelpTooltip helpId="items-page-help" title={t('help.items.title')} content={<ItemHelpContent language={language} canManage={canWrite} isSuperAdmin={isSuperAdmin} />} maxWidth={700} placement="bottom-start" />}
      description={t('items.catalogSubtitle')}
      actions={canWrite ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <PageHeaderActionButton startIcon={<CategoryOutlined />} onClick={onAddCategory}>{t('category.manage')}</PageHeaderActionButton>
            <PageHeaderActionButton startIcon={<UploadFileOutlined />} onClick={onBulkUpload}>{t('items.bulkUpload')}</PageHeaderActionButton>
          </Stack>
          <PageHeaderActionButton data-testid="item-add" startIcon={<Add />} onClick={onAdd} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>{t('items.addItem')}</PageHeaderActionButton>
        </Stack>
      ) : undefined}
    />

    <ListSurface sx={{ overflow: { xs: 'hidden', md: 'visible' } }}>
      <ListSurface.Toolbar sx={{ p: { xs: 0, md: 2.5 } }}>
        {isMobile ? <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 2, pt: 1.5 }}>
          <TextField
            inputRef={searchInputRef} autoFocus={autoFocusSearch} inputProps={{ 'data-testid': 'item-search', 'aria-label': t('items.searchPlaceholder') }}
            placeholder={t('items.searchPlaceholder')} value={searchParams.name || ''} onChange={event => onSearch(event.target.value)}
            size="small" fullWidth sx={{ flex: 1, minWidth: 0, '& .MuiOutlinedInput-root': { height: 44, borderRadius: 2, bgcolor: 'action.hover', '& fieldset, &:hover fieldset': { border: 0 }, '&.Mui-focused fieldset': { border: '2px solid', borderColor: 'primary.main' } } }} InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              endAdornment: searchParams.name ? <InputAdornment position="end"><IconButton aria-label={t('items.clearFilters')} onClick={() => onSearch('')}><Clear /></IconButton></InputAdornment> : undefined,
            }}
          />
          <IconButton aria-label={t('barcode.scan')} onClick={() => setScannerOpen(true)} sx={{ width: 44, height: 44, flexShrink: 0 }}><QrCodeScanner /></IconButton>
          <Badge badgeContent={appliedFilterCount} color="warning" invisible={!appliedFilterCount} sx={{ '& .MuiBadge-badge': { minWidth: 17, height: 17, fontSize: 10, fontWeight: 700 } }}>
            <IconButton aria-label={t('common.filters')} aria-expanded={showFilters} aria-controls="item-filters" onClick={() => setShowFilters(value => !value)} sx={{ width: 44, height: 44, flexShrink: 0 }}><FilterList /></IconButton>
          </Badge>
          {canWrite && <IconButton aria-label={t('items.addItem')} onClick={onAdd} sx={{ width: 44, height: 44, flexShrink: 0, color: 'primary.main' }}><Add sx={{ fontSize: 30 }} /></IconButton>}
        </Stack> : <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField
            inputRef={searchInputRef} autoFocus={autoFocusSearch} inputProps={{ 'data-testid': 'item-search' }}
            placeholder={t('items.searchPlaceholder')} value={searchParams.name || ''} onChange={event => onSearch(event.target.value)}
            fullWidth sx={{ flex: 1 }} InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              endAdornment: searchParams.name ? <InputAdornment position="end"><IconButton aria-label={t('items.clearFilters')} onClick={() => onSearch('')}><Clear /></IconButton></InputAdornment> : undefined,
            }}
          />
          <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <BarcodeScanButton label={t('barcode.scan')} onDetected={onSearch} />
            <Badge badgeContent={appliedFilterCount} color="warning" invisible={appliedFilterCount === 0}>
              <Button variant="outlined" startIcon={<FilterList />} endIcon={showFilters ? <ExpandLess /> : <ExpandMore />} aria-expanded={showFilters} aria-controls="item-filters" onClick={() => setShowFilters(value => !value)} sx={{ bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>{t('common.filters')}</Button>
            </Badge>
          </Stack>
        </Stack>}
        {isMobile ? <Collapse in={showFilters}>
          <Box id="item-filters" sx={{ mt: 1.5, mx: 2, mb: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'action.hover' }}>
            <Stack direction="column" spacing={1.5}>
              <Autocomplete options={categories} getOptionLabel={option => option.name} value={selectedCategory} onChange={(_, category) => onSearchParamsChange({ categoryId: category?.id, page: 0 })} sx={{ flex: 1, minWidth: 170 }} renderInput={params => <TextField {...params} label={t('categories.categories')} placeholder={t('items.allCategories')} />} />
              <TextField label={t('items.minPrice')} type="number" value={searchParams.minPrice ?? ''} onChange={event => onSearchParamsChange({ minPrice: event.target.value === '' ? undefined : Number(event.target.value), page: 0 })} inputProps={{ min: 0, step: .01 }} sx={{ flex: 1 }} />
              <TextField label={t('items.maxPrice')} type="number" value={searchParams.maxPrice ?? ''} onChange={event => onSearchParamsChange({ maxPrice: event.target.value === '' ? undefined : Number(event.target.value), page: 0 })} inputProps={{ min: 0, step: .01 }} sx={{ flex: 1 }} />
              {isSuperAdmin && <FormControl sx={{ flex: 1, minWidth: 170 }}><InputLabel>{t('items.company')}</InputLabel><Select value={searchParams.companyId || ''} label={t('items.company')} onChange={event => onCompanyFilter?.(event.target.value)}><MenuItem value="">{t('common.all')}</MenuItem>{companies.map(company => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}</Select></FormControl>}
            </Stack>
          </Box>
        </Collapse> : <TableFilterPanel
          id="item-filters" open={showFilters} activeFilters={activeFilterChips} columns={isSuperAdmin ? 4 : 3}
          onClear={clearFilters} onApply={applyFilters} error={filterError}
          fields={<>
            <Autocomplete options={categories} getOptionLabel={option => option.name} value={categories.find(category => category.id === categoryDraft) ?? null} onChange={(_, category) => setCategoryDraft(category?.id || '')} renderInput={params => <TextField {...params} label={t('categories.categories')} placeholder={t('items.allCategories')} />} />
            <TextField label={t('items.minPrice')} type="number" value={minPriceDraft} onChange={event => { setMinPriceDraft(event.target.value); setFilterError(''); }} inputProps={{ min: 0, step: .01 }} />
            <TextField label={t('items.maxPrice')} type="number" value={maxPriceDraft} onChange={event => { setMaxPriceDraft(event.target.value); setFilterError(''); }} inputProps={{ min: 0, step: .01 }} />
            {isSuperAdmin && <FormControl><InputLabel>{t('items.company')}</InputLabel><Select value={companyDraft} label={t('items.company')} onChange={event => setCompanyDraft(event.target.value)}><MenuItem value="">{t('common.all')}</MenuItem>{companies.map(company => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}</Select></FormControl>}
          </>}
        />}
        {isMobile && activeFilterChips.length > 0 && <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} sx={{ px: 2, pt: 1, pb: 0.5 }}>
          {activeFilterChips.map(filter => <Chip key={filter.key} size="small" label={filter.label} onDelete={filter.onDelete} />)}
          <Button size="small" onClick={clearFilters} sx={{ minWidth: 0, px: 1 }}>{t('items.clearFilters')}</Button>
        </Stack>}
      </ListSurface.Toolbar>

      <ListSurface.Content sx={{ position: 'relative' }}>
      <NotebookColumnTab label={t('common.columns')} accent="items" open={Boolean(columnMenuAnchor)} onClick={event => setColumnMenuAnchor(event.currentTarget)} />
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : isMobile ? <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {items.map(item => {
            const hasInventory = getInventoryMode(item) !== InventoryMode.NONE;
            const secondaryText = [item.itemNumber, item.categories?.map(category => category.name).join(', ')]
              .filter(Boolean).join(' · ');
            const summary = <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%', minWidth: 0, py: 1, pr: 1.5 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography component="h3" fontWeight={600} noWrap sx={{ fontSize: 15, lineHeight: 1.3 }}>{item.name}</Typography>
                {secondaryText && <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25, fontSize: 13 }}>{secondaryText}</Typography>}
              </Box>
              <Box sx={{ flexShrink: 0, maxWidth: '42%', textAlign: 'right' }}>
                <Typography variant="body2" fontWeight={700} noWrap>{getItemPriceLabel(item, t)}</Typography>
                <Typography variant="caption" noWrap sx={{ display: 'block', mt: 0.25, color: !hasInventory ? 'text.secondary' : item.totalStockQuantity <= 0 ? 'error.main' : item.totalStockQuantity <= 10 ? 'warning.dark' : 'text.secondary' }}>
                  {hasInventory ? `${item.totalStockQuantity} ${t('inventory.units')}` : t('items.inventoryNotTracked')}
                </Typography>
              </Box>
            </Box>;
            return <Box component="li" key={item.id} sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 72, pl: 1.5, pr: 1, gap: 0.75 }}>
                {hasInventory ? <CardActionArea disableRipple aria-label={`${item.name}, ${t('items.openInventory')}`} onClick={() => openInventory(item)} sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch', ml: -1.5, pl: 2, pr: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', borderRadius: 1, textAlign: 'left', '&:active': { bgcolor: 'action.hover' } }}>{summary}</CardActionArea>
                  : <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', pl: 0.5 }}>{summary}</Box>}
                {canWrite && <Box sx={{ flexShrink: 0, '& .MuiIconButton-root': { width: 44, height: 44, color: 'text.secondary' } }}><ItemActionsMenu item={item} onEdit={onEdit} onDelete={onDelete} /></Box>}
              </Box>
            </Box>;
          })}
        </Box> : <Box sx={{ p: 2, pt: 1.5 }}><Stack spacing={1.5}>
          {items.map(item => {
            const hasInventory = getInventoryMode(item) !== InventoryMode.NONE;
            return <MobileListCard key={item.id} title={item.name} subtitle={item.itemNumber} onClick={hasInventory ? () => openInventory(item) : undefined} fields={[
              { label: t('items.salePrice'), value: getItemPriceLabel(item, t) },
              { label: t('inventory.stock'), value: hasInventory ? `${item.totalStockQuantity} ${t('inventory.units')}` : t('items.inventoryNotTracked') },
              { label: t('category.title'), value: item.categories?.map(category => category.name).join(', ') ?? '' },
            ]} actions={<Stack direction="row" alignItems="center">{hasInventory && <Button size="small" endIcon={<OpenInNew fontSize="small" />} onClick={() => openInventory(item)}>{t('items.openInventory')}</Button>}{canWrite && <ItemActionsMenu item={item} onEdit={onEdit} onDelete={onDelete} />}</Stack>} />;
          })}
        </Stack></Box>}
      </Box>
      <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table>
          <TableHead><TableRow>{table.getHeaderGroups().flatMap(group => group.headers.map(header => <DataTableHeaderCell key={header.id} align={header.column.id === 'price' || header.column.id === 'actions' ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap' }} sort={header.column.getCanSort() ? {
            active: Boolean(header.column.getIsSorted()),
            direction: header.column.getIsSorted() === 'desc' ? 'desc' : 'asc',
            onClick: () => header.column.toggleSorting(),
          } : undefined}>{flexRender(header.column.columnDef.header, header.getContext())}</DataTableHeaderCell>))}</TableRow></TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}><CircularProgress /></TableCell></TableRow>
              : table.getRowModel().rows.map(row => <TableRow key={row.id} hover data-testid={`item-row-${row.original.id}`} sx={{ '&:last-child td': { borderBottom: 0 } }}>{row.getVisibleCells().map(cell => <TableCell key={cell.id} align={cell.column.id === 'price' || cell.column.id === 'actions' ? 'right' : 'left'}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>)}</TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && items.length === 0 && <Box sx={{ textAlign: 'center', py: 7, px: 2 }}><Inventory2Outlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} /><Typography variant="h6">{hasFilters ? t('items.noSearchResults') : t('items.noItems')}</Typography><Typography color="text.secondary" sx={{ mb: 2 }}>{hasFilters ? t('items.tryDifferentSearch') : t('items.addFirstItem')}</Typography>{hasFilters ? <Button onClick={clearFilters}>{t('items.clearFilters')}</Button> : canWrite && <Button variant="contained" startIcon={<Add />} onClick={onAdd}>{t('items.addItem')}</Button>}</Box>}
      </ListSurface.Content>
      <ListSurface.Footer>
        {paginatedData && (isMobile || paginatedData.totalElements > 0) && <PaginationFooter pageIndex={searchParams.page ?? 0} pageSize={searchParams.size ?? 10} totalPages={paginatedData.totalPages} totalElements={paginatedData.totalElements} showingLabel={t('common.showing')} ofLabel={t('common.of')} pageSizeLabel={t('common.rowsPerPage')} mobileCompact={isMobile} previousPageLabel={t('customers.previousPage')} nextPageLabel={t('customers.nextPage')} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} />}
      </ListSurface.Footer>
    </ListSurface>

    <ColumnVisibilityMenu side anchorEl={columnMenuAnchor} onClose={() => setColumnMenuAnchor(null)} onToggle={key => setColumnVisibility(previous => ({ ...previous, [key]: previous[key] === false }))} items={[
      { key: 'name', label: t('items.name'), visible: columnVisibility.name },
      { key: 'price', label: t('items.salePrice'), visible: columnVisibility.price },
      { key: 'stock', label: t('inventory.stock'), visible: columnVisibility.stock },
      { key: 'categories', label: t('category.title'), visible: columnVisibility.categories },
      ...(isSuperAdmin ? [{ key: 'companyName', label: t('items.company'), visible: columnVisibility.companyName }] : []),
    ]} />
    <InventoryManagementDialog open={Boolean(inventoryItem)} onClose={() => setInventoryItem(null)} item={inventoryItem} onItemUpdated={handleInventoryItemUpdated} batchTrackingEnabled={batchTrackingEnabled} />
    <BarcodeScannerDialog open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={onSearch} />
  </Box>;
});

export default ItemList;
